import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Target, Heart, Users, Award } from 'lucide-react';

export default function About() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <WhatsAppButton />

      <section className="bg-gradient-to-br from-orange-500 to-red-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-extrabold font-heading mb-6"
            data-testid="about-title"
          >
            About Us
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-xl font-hindi"
          >
            मनीष कश्यप डिजिटल रिसर्च प्लेटफॉर्म के बारे में
          </motion.p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <img 
                src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/hqwddwri_image%20%282%29.jpeg" 
                alt="Platform Purpose" 
                className="rounded-2xl shadow-2xl"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-bold font-heading text-gray-900 mb-6">
                Our <span className="gradient-text">Mission</span>
              </h2>
              <div className="space-y-4 text-gray-700 font-hindi text-lg leading-relaxed">
                <p>
                  आज के डिजिटल युग में इंटरनेट और सोशल मीडिया केवल मनोरंजन का माध्यम नहीं रह गया है, बल्कि यह रोजगार, व्यवसाय और आत्मनिर्भरता का एक बड़ा साधन बन चुका है। इसी सोच और उद्देश्य के साथ मनीष कश्यप ने "मनीष कश्यप डिजिटल रिसर्च प्लेटफॉर्म" की शुरुआत की है।
                </p>
                <p>
                  इस प्लेटफॉर्म का मुख्य लक्ष्य युवाओं, महिलाओं और छोटे व्यवसायियों को डिजिटल दुनिया की सही जानकारी देकर उन्हें आत्मनिर्भर बनाना है।
                </p>
              </div>
            </motion.div>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-20">
            {[
              { icon: <Target className="w-12 h-12" />, title: "Our Vision", desc: "युवाओं को डिजिटल मीडिया से आत्मनिर्भर बनाना" },
              { icon: <Heart className="w-12 h-12" />, title: "Our Values", desc: "ईमानदारी, समर्पण और शिक्षा" },
              { icon: <Users className="w-12 h-12" />, title: "Our Community", desc: "हजारों सफल छात्र और व्यवसायी" },
              { icon: <Award className="w-12 h-12" />, title: "Our Excellence", desc: "भारत का No.1 डिजिटल प्लेटफॉर्म" }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-gradient-to-br from-orange-50 to-white p-8 rounded-2xl shadow-lg text-center hover:shadow-2xl transition-all"
                data-testid={`value-card-${index}`}
              >
                <div className="text-orange-600 mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600 font-hindi">{item.desc}</p>
              </motion.div>
            ))}
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-white p-12 rounded-3xl shadow-xl">
            <h2 className="text-4xl font-bold font-heading text-center text-gray-900 mb-12">
              Meet Our <span className="gradient-text">Mentors</span>
            </h2>
            
            <div className="space-y-12">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/z76sumi5_manish%20Kasyap.png" 
                    alt="Manish Kasyap" 
                    className="w-40 h-40 rounded-full border-4 border-orange-500 shadow-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Manish Kasyap</h3>
                    <p className="text-orange-600 font-semibold text-lg mb-4">Digital Guru & Founder</p>
                    <p className="text-gray-700 font-hindi leading-relaxed mb-4">
                      मनीष कश्यप देश के प्रसिद्ध यूट्यूबर, सामाजिक कार्यकर्ता और जनसरोकार से जुड़े मुद्दों को उजागर करने वाले डिजिटल क्रिएटर हैं। उन्होंने अपने यूट्यूब चैनल और सोशल मीडिया के माध्यम से देश और समाज से जुड़े कई महत्वपूर्ण मुद्दों को जनता के सामने मजबूती से उठाया है।
                    </p>
                    <p className="text-gray-700 font-hindi leading-relaxed">
                      मनीष कश्यप की ख्याचिरात यह है कि उन्होंने डिजिटल मीडिया की ताकत को बहुत करीब से समझा और उसका प्रभावी उपयोग किया। सोशल मीडिया के माध्यम से उन्होंने न केवल अपनी पहचान बनाई, बल्कि यह भी साबित किया कि यदि सही तरीके से डिजिटल प्लेटफॉर्म का उपयोग किया जाए तो यह आम लोगों की आवाज को भी देश के सामने मजबूती से रख सकता है।
                    </p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-white p-8 rounded-2xl shadow-lg"
              >
                <div className="flex flex-col md:flex-row gap-8 items-center">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_digital-research-hub/artifacts/fia3kxn3_Pankaj%20Sir.jpg" 
                    alt="Pankaj Sir" 
                    className="w-40 h-40 rounded-full border-4 border-blue-500 shadow-xl object-cover"
                  />
                  <div className="flex-1">
                    <h3 className="text-3xl font-bold text-gray-900 mb-2">Pankaj Sir</h3>
                    <p className="text-blue-600 font-semibold text-lg mb-4 font-hindi">संस्थापक - 35 वर्षों का शैक्षणिक अनुभव</p>
                    <p className="text-gray-700 font-hindi leading-relaxed mb-4">
                      पंकज सर शिक्षा जगत के एक अनुभवी और समर्पित शिक्षक हैं, जिन्हें इस क्षेत्र में लगभग 35 वर्षों का लंबा अनुभव है। अपने धर्मण, अनुशासन और शिक्षण के प्रभावी तरीके के कारण उन्होंने हजारों छात्रों के भविष्य को नई दिशा देने का कार्य किया है।
                    </p>
                    <p className="text-gray-700 font-hindi leading-relaxed">
                      उनका यह विश्वास है कि शिक्षा केवल किताबों तक सीमित नहीं होनी चाहिए, बल्कि छात्रों को समय के साथ बदलती तकनीकी और नए अवसरों के बारे में भी जानकारी मिलनी चाहिए। इसी सोच के साथ वे "मनीष कश्यप डिजिटल रिसर्च प्लेटफॉर्म" से जुड़े हैं, जहां उनका उद्देश्य युवाओं को आधुनिक डिजिटल शिक्षा से जोड़ना और उन्हें भविष्य के लिए तैयार करना है।
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}